import type { ReactNode } from 'react';

export interface DataGridColumn<TRow> {
  /** Unique column id. Used for sort URL value + visibility persistence. */
  id: string;
  /** Header content (string or any node). */
  header: ReactNode;
  /** Cell renderer. */
  cell: (row: TRow, index: number) => ReactNode;
  /** Whether the column header is clickable to toggle sort. */
  sortable?: boolean;
  /** Whether this column is hidden by default. User can re-enable via the visibility dropdown. */
  defaultHidden?: boolean;
  /** Whether this column can be toggled off by the user (kebab columns set true). */
  alwaysVisible?: boolean;
  /** Tailwind classes merged onto every td. */
  className?: string;
  /** Width applied to the th inline style. */
  width?: string | number;
  align?: 'left' | 'right' | 'center';
  /**
   * Truncate overflowing cell content with an ellipsis (default true). Set
   * false for cells holding interactive controls (e.g. an actions menu) - the
   * universal `text-overflow: ellipsis` otherwise paints a stray "…" at the
   * clip edge when the control is slightly wider than the column.
   */
  truncate?: boolean;
  /** Allow drag-to-resize this column's width. Defaults to true. Set false for
   *  fixed control columns (e.g. an icon/actions column). */
  resizable?: boolean;
}

export interface DataGridFilterOption {
  /** Value written to the URL (and forwarded to the server query). */
  value: string;
  label: string;
}

export interface DataGridFilter {
  /** Filter id - also the URL key (without the 'f.' prefix the URL uses). */
  id: string;
  label: string;
  /** Selectable values. Always include an "all" option at the call site if desired
   *  - the grid does NOT auto-prepend one (callers control the empty-state label). */
  options: ReadonlyArray<DataGridFilterOption>;
  /** Width class for the select container. Defaults to "w-44". */
  widthClass?: string;
  /**
   * Multi-select mode. When true the chip lets the user toggle several values;
   * they are stored in the URL as a comma-separated string (`a,b,c`). The "all"
   * option (`value: ''`) is ignored in multi mode - clearing all checkboxes is
   * the empty state.
   */
  multi?: boolean;
  /**
   * Put a search box at the top of the chip's popover. Set it for lists that
   * grow with the data (cities, countries, people) rather than fixed enums -
   * scrolling a few hundred options to reach "Srinagar" is not a filter.
   *
   * Left unset, the chip decides for itself: any list past
   * `SEARCHABLE_OPTION_THRESHOLD` gets one. Pass `false` to force it off.
   */
  searchable?: boolean;
  /**
   * Deep-link-only filter: it lives in the URL and reaches `toQuery` like any
   * other, but gets no chip in the toolbar - so a screen can accept filters
   * from elsewhere (a dashboard number) without growing its filter bar. While
   * one is active the toolbar shows a removable pill for it, because a list
   * that is silently narrowed reads as missing data. `options` may be empty;
   * see `hiddenFilterValue` for how the pill gets a readable label.
   */
  hidden?: boolean;
}

/**
 * A hidden filter's URL value, carrying its own display label after a `~`
 * (`<uuid>~Europe`). The list screen has no options list to look the label up
 * in, and fetching one just to name a pill would defeat the point.
 */
export const hiddenFilterValue = (id: string, label: string): string =>
  `${id}~${label.replace(/~/g, ' ')}`;

/** The id half of a `hiddenFilterValue` - what goes to the server. */
export const hiddenFilterId = (value: string | undefined): string | undefined => {
  if (!value) return undefined;
  const cut = value.indexOf('~');
  return cut === -1 ? value : value.slice(0, cut);
};

/** The label half of a `hiddenFilterValue`, falling back to the raw value. */
export const hiddenFilterLabel = (value: string): string => {
  const cut = value.indexOf('~');
  return cut === -1 ? value : value.slice(cut + 1);
};

export interface DataGridBulkAction<TRow> {
  /** Stable id. */
  id: string;
  label: string;
  icon?: ReactNode;
  /** Render with destructive styling (e.g. Delete). */
  danger?: boolean;
  /**
   * Run the action against the current selection. `rows` are the selected rows
   * present on the page; `ids` is the full selected id set. Selection is pruned
   * automatically when the data refetches (e.g. after a delete), so no manual
   * clear is needed for mutations that change the list.
   */
  onAction: (ids: string[], rows: TRow[]) => void;
}

export type Density = 'compact' | 'comfortable';

export interface DataGridState {
  q: string;
  page: number;
  pageSize: number;
  sortBy: string | null;
  sortDir: 'asc' | 'desc';
  density: Density;
  hidden: string[];
  filters: Record<string, string>;
}
